import {
  constant,
  FREQUENCIES,
  withMonth,
  type Flow,
  type Growth,
  type RateSpec,
} from '@finsim/model';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  categoryInflationPath,
  RateField,
  RateOrInheritField,
  ScheduledNumberField,
} from '../../../entities/project';
import { Field, IntegerField, MoneyField, PercentField, Section, Select } from '../../../shared/ui';
import { AccountField, CategoryField, DirectionField, WindowFields } from './common-fields';
import { withOptional } from '../lib';
import type { FormContext } from './context';

type GrowthKind = Growth['kind'];

function defaultGrowth(kind: GrowthKind): Growth {
  switch (kind) {
    case 'none':
      return { kind: 'none' };
    case 'indexed':
      return { kind: 'indexed', timing: 'annual' };
    case 'rate':
      return {
        kind: 'rate',
        rate: constant<RateSpec>({ kind: 'fixed', rate: 0.02 }),
        timing: 'annual',
      };
    case 'steps':
      return { kind: 'steps', raise: 0.05, everyMonths: 24 };
  }
}

export function FlowForm({ item, ctx }: { item: Flow; ctx: FormContext }) {
  const { t } = useTranslation();
  const set = (next: Flow, field: string): void => {
    ctx.commit(next, field);
  };
  const g = item.growth;
  const category = ctx.project.categories.find((c) => c.id === item.category);
  const categoryDefaultLabel = category?.inflation
    ? t('fields.categoryDefault', { name: category.name })
    : t('fields.globalCpi');
  const inheritedPath = useMemo(
    () => categoryInflationPath(ctx.project, item.category),
    [ctx.project, item.category],
  );

  return (
    <>
      <Section title={t('inspector.sections.amount')}>
        <DirectionField
          value={item.direction}
          onChange={(direction) => {
            set({ ...item, direction }, 'direction');
          }}
        />
        <ScheduledNumberField
          label={t('fields.amount')}
          value={item.amount}
          project={ctx.project}
          format={ctx.money}
          onCommit={(amount) => {
            set({ ...item, amount }, 'amount');
          }}
          renderInput={(value, onCommit, label, id) => (
            <MoneyField
              id={id}
              aria-label={label}
              {...(id === undefined ? {} : { className: 'primary-field' })}
              value={value}
              symbol={ctx.symbol}
              onCommit={onCommit}
            />
          )}
        />
        <Field label={t('fields.frequency')}>
          {({ id }) => (
            <Select
              id={id}
              value={item.frequency}
              options={FREQUENCIES.map((f) => ({ value: f, label: t(`fields.frequencies.${f}`) }))}
              onValueChange={(frequency) => {
                set(
                  frequency === 'once'
                    ? withMonth({ ...item, frequency }, 'end', undefined)
                    : { ...item, frequency },
                  'frequency',
                );
              }}
            />
          )}
        </Field>
      </Section>

      <Section title={t('inspector.sections.timing')}>
        <WindowFields ctx={ctx} item={item} showEnd={item.frequency !== 'once'} onChange={set} />
      </Section>

      <Section title={t('inspector.sections.growth')}>
        <Field label={t('fields.growth')}>
          {({ id }) => (
            <Select
              id={id}
              value={g.kind}
              options={(['none', 'indexed', 'rate', 'steps'] as const).map((k) => ({
                value: k,
                label: t(`fields.growthKinds.${k}`),
              }))}
              onValueChange={(kind) => {
                if (kind !== g.kind) set({ ...item, growth: defaultGrowth(kind) }, 'growth');
              }}
            />
          )}
        </Field>
        {g.kind === 'rate' && (
          <RateField
            label={t('rate.kind')}
            value={g.rate}
            project={ctx.project}
            onCommit={(rate) => {
              set({ ...item, growth: { ...g, rate } }, 'growthRate');
            }}
          />
        )}
        {g.kind === 'indexed' && (
          <RateOrInheritField
            label={t('fields.inflation')}
            value={g.override}
            project={ctx.project}
            inheritLabel={categoryDefaultLabel}
            inherited={inheritedPath}
            hint={t('fields.inflationHint')}
            onCommit={(override) => {
              set({ ...item, growth: withOptional(g, 'override', override) }, 'override');
            }}
          />
        )}
        {(g.kind === 'indexed' || g.kind === 'rate') && (
          <Field label={t('fields.timing')}>
            {({ id }) => (
              <Select
                id={id}
                value={g.timing}
                options={(['monthly', 'annual'] as const).map((k) => ({
                  value: k,
                  label: t(`fields.timings.${k}`),
                }))}
                onValueChange={(timing) => {
                  set({ ...item, growth: { ...g, timing } }, 'timing');
                }}
              />
            )}
          </Field>
        )}
        {g.kind === 'steps' && (
          <>
            <Field label={t('fields.raise')}>
              {({ id }) => (
                <PercentField
                  id={id}
                  value={g.raise}
                  onCommit={(raise) => {
                    set({ ...item, growth: { ...g, raise } }, 'raise');
                  }}
                />
              )}
            </Field>
            <Field label={t('fields.everyMonths')}>
              {({ id }) => (
                <IntegerField
                  id={id}
                  value={g.everyMonths}
                  min={1}
                  max={ctx.project.assumptions.horizonMonths}
                  onCommit={(everyMonths) => {
                    set({ ...item, growth: { ...g, everyMonths } }, 'everyMonths');
                  }}
                />
              )}
            </Field>
          </>
        )}
        {g.kind !== 'none' && (
          <Field label={t('fields.basis')}>
            {({ id }) => (
              <Select
                id={id}
                value={item.amountBasis}
                onValueChange={(amountBasis) => {
                  set({ ...item, amountBasis }, 'amountBasis');
                }}
                options={[
                  { value: 'today', label: t('fields.basisToday') },
                  { value: 'atStart', label: t('fields.basisAtStart') },
                ]}
              />
            )}
          </Field>
        )}
      </Section>

      <Section title={t('inspector.sections.classification')}>
        <AccountField
          ctx={ctx}
          value={item.account}
          onChange={(account) => {
            set(withOptional(item, 'account', account), 'account');
          }}
        />
        {item.account === undefined && (
          <CategoryField
            ctx={ctx}
            value={item.category}
            kind={item.direction === 'in' ? 'income' : 'expense'}
            onChange={(category) => {
              set(withOptional(item, 'category', category as Flow['category']), 'category');
            }}
          />
        )}
      </Section>
    </>
  );
}
